import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, Menu, ChevronLeft, Plus, Sparkles, ArrowLeft } from 'lucide-react';
import SideNavigation from '../components/SideNavigation';
import NewMessageModal from '../components/NewMessageModal';
import useScreenSize from '../hooks/useScreenSize';
import { 
  fetchConversations, 
  fetchMessages, 
  sendMessage, 
  markMessagesAsRead,
  setCurrentConversation,
  markConversationAsRead,
  checkActiveLoads,
  toggleNewMessageModal
} from '../store/slices/messagingSlice';
import { fetchNotifications } from '../store/slices/notificationSlice';

const MessagingPage = () => {
  const dispatch = useDispatch();
  const { isMobile, isTablet, isDesktop } = useScreenSize();
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  const { 
    conversations, 
    currentConversation, 
    status, 
    activeLoads,
    showNewMessageModal 
  } = useSelector(state => state.messaging);
  const userOrgId = useSelector(state => state.auth.user?.entityId);
  const userType = useSelector(state => state.auth.userType);
  const user = useSelector(state => state.auth.user);
  
  useEffect(() => {
    dispatch(fetchNotifications({ page: currentPage }));
  }, [dispatch, currentPage]);
  
  useEffect(() => {
    dispatch(fetchConversations());
  }, [dispatch]);
  
  useEffect(() => {
    setSidebarOpen(isDesktop);
  }, [isDesktop]);
  
  useEffect(() => {
    if (currentConversation.otherOrgId) {
      dispatch(fetchMessages({ otherOrgId: currentConversation.otherOrgId }));
    }
  }, [dispatch, currentConversation.otherOrgId]);
  
  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [currentConversation.messages, currentConversation.otherOrgId]);
  
  // Handle mobile keyboard appearance
  useEffect(() => {
    if (isMobile && isInputFocused) {
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    }
  }, [isInputFocused, isMobile]);
  
  const handleConversationSelect = (orgId) => {
    if (orgId !== currentConversation.otherOrgId) {
      dispatch(setCurrentConversation(orgId));
      
      // Mark all messages in the conversation as read
      dispatch(markConversationAsRead(orgId))
        .unwrap()
        .then(() => {
          // Refresh conversations to update unread counts
          dispatch(fetchConversations());
        })
        .catch(error => {
          console.error('Failed to mark conversation as read:', error);
        });
    }
  };
  
  const handleInputFocus = () => {
    setIsInputFocused(true);
    if (currentConversation.otherOrgId) {
      dispatch(markConversationAsRead(currentConversation.otherOrgId));
    }
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
  };
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (newMessage.trim() && currentConversation.otherOrgId) {
      try {
        await dispatch(sendMessage({
          receiverOrgId: currentConversation.otherOrgId,
          content: newMessage
        })).unwrap();
        setNewMessage('');
        
        // Blur input on mobile after sending
        if (isMobile && messageInputRef.current) {
          messageInputRef.current.blur();
        }
      } catch (err) {
        setError(err.message || 'Failed to send message');
      }
    }
  };
  
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleNewMessage = () => {
    dispatch(toggleNewMessageModal(true));
  };

  // Add this function to pass to NewMessageModal
  const handleConversationSelectFromModal = (orgId) => {
    handleConversationSelect(orgId);
  };

  const handleBackToConversations = () => {
    dispatch(setCurrentConversation(null));
    setIsInputFocused(false);
  };

  useEffect(() => {
    if (currentConversation.otherOrgId) {
      dispatch(checkActiveLoads(currentConversation.otherOrgId));
    }
  }, [dispatch, currentConversation.otherOrgId]);

  if (status === 'loading') {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading messages...</p>
        </div>
      </div>
    );
  }

  // Mobile-first layout decision
  const showConversationsList = !currentConversation.otherOrgId || isDesktop;
  const showMessagesArea = currentConversation.otherOrgId || isDesktop;

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen flex overflow-hidden">
      <SideNavigation isOpen={sidebarOpen} onToggle={toggleSidebar} />

      <main className={`flex-1 overflow-hidden transition-all duration-300 ${
        isMobile || isTablet ? 'ml-0' : (sidebarOpen ? 'ml-64' : 'ml-16')
      }`}>
        <div className="h-screen flex flex-col">
          {/* Enhanced Mobile-First Header */}
          <div className="bg-white/90 backdrop-blur-sm shadow-sm border-b border-gray-200/50 p-3 sm:p-4 flex items-center justify-between relative z-10">
            <div className="flex items-center min-w-0 flex-1">
              {(isMobile || isTablet) && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleSidebar}
                  className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                >
                  <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
                </motion.button>
              )}
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <div className="p-1.5 sm:p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex-shrink-0">
                  <MessageSquare className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold text-gray-800 truncate">Messages</h1>
                  <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Stay connected with your partners</p>
                </div>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNewMessage}
              className="inline-flex items-center px-3 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-blue-600/20 flex-shrink-0"
            >
              <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">New Message</span>
              <span className="sm:hidden">New</span>
              <Sparkles className="h-2 w-2 sm:h-3 sm:w-3 ml-1 sm:ml-2 opacity-70" />
            </motion.button>
          </div>

          {/* Main content with mobile-optimized layout */}
          <div className="flex-1 flex overflow-hidden relative">
            {/* Enhanced Mobile-Responsive Conversations list */}
            <AnimatePresence mode="wait">
              {showConversationsList && (
                <motion.div 
                  key="conversations"
                  initial={{ x: isMobile ? -100 : 0, opacity: isMobile ? 0 : 1 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: isMobile ? -100 : 0, opacity: isMobile ? 0 : 1 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className={`bg-white/60 backdrop-blur-sm border-r border-gray-200/50 flex-shrink-0 ${
                    isMobile 
                      ? 'absolute inset-0 z-20 w-full' 
                      : isTablet 
                        ? 'w-72' 
                        : 'w-80'
                  }`}
                >
                  <div className="h-full flex flex-col">
                    <div className="p-3 sm:p-4 border-b border-gray-200/50 bg-white/40">
                      <h2 className="text-base sm:text-lg font-semibold text-gray-700">Conversations</h2>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">
                        {conversations.length} active conversation{conversations.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      <AnimatePresence>
                        {conversations.map((conversation, index) => (
                          <motion.div
                            key={conversation.org_id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{ x: isDesktop ? 4 : 0 }}
                            whileTap={{ scale: 0.98 }}
                            className={`p-3 sm:p-4 border-b border-gray-200/50 cursor-pointer hover:bg-white/60 transition-all duration-200 ${
                              currentConversation.otherOrgId === conversation.org_id 
                                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-l-blue-500' 
                                : ''
                            }`}
                            onClick={() => handleConversationSelect(conversation.org_id)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 truncate text-sm sm:text-base">
                                  {conversation.org_name}
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-500 mt-1 line-clamp-2">
                                  {conversation.last_message?.content}
                                </p>
                              </div>
                              {conversation.unread_count > 0 && (
                                <motion.span
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-sm ml-2 flex-shrink-0 min-w-[1.5rem] text-center"
                                >
                                  {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                                </motion.span>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Enhanced Mobile-Responsive Messages area */}
            <AnimatePresence mode="wait">
              {showMessagesArea && (
                <motion.div 
                  key="messages"
                  initial={{ x: isMobile ? 100 : 0, opacity: isMobile ? 0 : 1 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: isMobile ? 100 : 0, opacity: isMobile ? 0 : 1 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className={`flex-1 flex flex-col ${
                    !currentConversation.otherOrgId ? 'hidden lg:flex items-center justify-center' : ''
                  } ${isMobile ? 'absolute inset-0 z-20' : ''}`}
                >
                  {!currentConversation.otherOrgId ? (
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center text-gray-500 max-w-md mx-auto px-6"
                    >
                      <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                        <MessageSquare className="h-10 w-10 sm:h-12 sm:w-12 text-blue-500" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2 sm:mb-3">Welcome to Messages</h3>
                      <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6 leading-relaxed">
                        Select an existing conversation or start a new one to connect with your partners
                      </p>
                      <motion.button
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleNewMessage}
                        className="inline-flex items-center px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />
                        Start New Conversation
                        <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 ml-2 sm:ml-3 opacity-70" />
                      </motion.button>
                    </motion.div>
                  ) : (
                    <>
                      {/* Enhanced Mobile-First Conversation header */}
                      <div className="p-3 sm:p-4 bg-white/90 backdrop-blur-sm border-b border-gray-200/50 flex items-center shadow-sm">
                        {(isMobile || isTablet) && (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleBackToConversations}
                            className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <ArrowLeft className="h-5 w-5" />
                          </motion.button>
                        )}
                        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <h2 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                              {conversations.find(c => c.org_id === currentConversation.otherOrgId)?.org_name}
                            </h2>
                            <p className="text-xs sm:text-sm text-gray-500">Active conversation</p>
                          </div>
                        </div>
                      </div>

                      {/* Messages with mobile-optimized spacing */}
                      <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-gradient-to-b from-white/40 to-blue-50/40">
                        {currentConversation.messages.map(message => {
                          const isOwnMessage = message.sender_org_id === userOrgId;
                          
                          return (
                            <motion.div
                              key={message.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 20 }}
                              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`flex flex-col max-w-[85%] sm:max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                                <span className={`text-xs text-gray-500 mb-1 sm:mb-2 font-medium ${isOwnMessage ? 'text-right' : 'text-left'}`}>
                                  {message.sender.full_name || message.sender.name}
                                </span>
                                <motion.div 
                                  whileHover={{ scale: isDesktop ? 1.02 : 1 }}
                                  whileTap={{ scale: 0.98 }}
                                  className={`rounded-2xl p-3 sm:p-4 shadow-sm ${
                                    isOwnMessage 
                                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' 
                                      : 'bg-white/90 backdrop-blur-sm text-gray-900 border border-gray-200/50'
                                  }`}
                                >
                                  <p className="whitespace-pre-wrap break-words leading-relaxed text-sm sm:text-base">
                                    {message.content}
                                  </p>
                                </motion.div>
                                <span className={`text-xs text-gray-400 mt-1 sm:mt-2 ${isOwnMessage ? 'text-right' : 'text-left'}`}>
                                  {new Date(message.created_at).toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: false
                                  })}
                                </span>
                              </div>
                            </motion.div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* Enhanced Mobile-First Message input */}
                      <div className={`p-3 sm:p-4 bg-white/90 backdrop-blur-sm border-t border-gray-200/50 transition-all duration-300 ${
                        isInputFocused ? 'pb-4 sm:pb-4' : ''
                      }`}>
                        {currentConversation.otherOrgId && (
                          <>
                            {!activeLoads[currentConversation.otherOrgId] ? (
                              <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center justify-center p-4 sm:p-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl sm:rounded-2xl border border-amber-200/50"
                              >
                                <div className="text-center">
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto mb-2 sm:mb-3">
                                    <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
                                  </div>
                                  <h3 className="font-semibold text-gray-800 mb-1 sm:mb-2 text-sm sm:text-base">Messaging Unavailable</h3>
                                  <p className="text-xs sm:text-sm text-gray-600">
                                    You can only send messages when you have active loads with this organization
                                  </p>
                                </div>
                              </motion.div>
                            ) : (
                              <>
                                {error && (
                                  <motion.div
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="mb-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm"
                                  >
                                    {error}
                                  </motion.div>
                                )}
                                <form onSubmit={handleSendMessage} className="flex space-x-2 sm:space-x-4">
                                  <input
                                    ref={messageInputRef}
                                    type="text"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    onFocus={handleInputFocus}
                                    onBlur={handleInputBlur}
                                    placeholder="Type your message..."
                                    className="flex-1 border border-gray-300 rounded-lg sm:rounded-xl px-3 py-2 sm:px-4 sm:py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/90 backdrop-blur-sm text-sm sm:text-base"
                                  />
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    type="submit"
                                    disabled={!newMessage.trim()}
                                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg sm:rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl flex-shrink-0"
                                  >
                                    <Send className="h-4 w-4 sm:h-5 sm:w-5" />
                                  </motion.button>
                                </form>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>

      {/* New Message Modal */}
      <NewMessageModal 
        isOpen={showNewMessageModal} 
        onClose={() => dispatch(toggleNewMessageModal(false))}
        onConversationSelect={handleConversationSelectFromModal}
      />
    </div>
  );
};

export default MessagingPage;